import { Layout } from 'antd';
import { FC, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useAdminUserController_getCurrentUser } from '@api-client/generated/AdminUserController/getCurrentUser';
import { accessMapping } from '@constants';
import { Header, NotFound, Sidebar } from '@entities';
import AccessDenied from '@entities/AccessDenied';
import { useAccount } from '@hooks';

import * as S from './styled';

const LayoutProtected: FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isLogged, account, role, setAccount } = useAccount();
  const { data: user, isPending: loading } =
    useAdminUserController_getCurrentUser();

  useEffect(
    () => {
      if (user && !account) {
        setAccount(user);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, account]
  );

  useEffect(
    () => {
      if (!isLogged) {
        navigate('/login');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLogged]
  );

  // Deliberately not gated on `role`: a realm user with no backoffice role assigned is expected
  // during the migration, and must reach the access-denied state below rather than a blank screen.
  if (!user || loading) {
    return null;
  }

  return (
    <S.Container>
      <Sidebar />

      <Layout>
        <Header />

        <S.Content>
          {/* A role with no mapping ('client', 'sales', or one added realm-side later) falls through
              to AccessDenied instead of throwing on an undefined path list. */}
          {(accessMapping[role as keyof typeof accessMapping] ?? []).some((route) =>
            pathname.includes(route)
          ) ? (
            <Outlet />
          ) : (
            <>
              {Array.from(new Set(Object.values(accessMapping).flat())).some(
                (route) => '/' + route === pathname
              ) ? (
                <AccessDenied />
              ) : (
                <NotFound />
              )}
            </>
          )}
        </S.Content>
      </Layout>
    </S.Container>
  );
};

export default LayoutProtected;
